import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import api from '../services/api'; 
import { useAuth } from '../contexts/AuthContext'; 
import { useDirtyForm } from '../contexts/DirtyFormContext';
import DateInput from '../components/DateInput';

// PDF.js 워커 (Edge 등 브라우저 내장 PDF 뷰어 의존 없이 렌더링)
const buildPdfWorkerSrc = () => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const base = publicUrl ? (window.location.origin + (publicUrl.startsWith('/') ? '' : '/') + publicUrl) : window.location.origin;
  return `${base}/pdf.worker.min.js`.replace(/\/+/g, '/');
};
if (typeof pdfjs?.GlobalWorkerOptions !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = buildPdfWorkerSrc();
}

// 첨부파일 검증 기준 (백엔드와 동일하게 유지)
const FILE_MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const FILE_MAX_SIZE_MB = 5;
const ALLOWED_FILE_TYPES = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'];
const ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];

const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', 
    top: 0, 
    zIndex: 10, 
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  select: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  searchInput: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '1200px', borderCollapse: 'collapse' }, 
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', fontSize: '0.9rem', whiteSpace: 'nowrap', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center' },
  button: { padding: '5px 10px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  saveButton: { backgroundColor: '#28a745', color: 'white' },
  modalButton: { backgroundColor: '#17a2b8', color: 'white' },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    marginTop: '15px', 
    paddingLeft: '5px' 
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #ddd'
  },
  modalBody: {
    marginBottom: '20px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  formLabel: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold'
  },
  formInput: {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  formRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px'
  },
  formCol: {
    flex: 1
  },
  fileInput: {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px'
  }
};

const LeaveRegisterPage = () => {
  const { user } = useAuth(); 
  const { setIsDirty } = useDirtyForm();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  // 입력용 검색 조건
  const [searchTerms, setSearchTerms] = useState({
    searchName: '',
    searchMemo: '',
    searchDistrict: '',
  });
  // 실제 조회에 사용되는 검색 조건 (검색 버튼 눌렀을 때만 반영)
  const [appliedSearch, setAppliedSearch] = useState({
    searchName: '',
    searchMemo: '',
    searchDistrict: '',
  });
  const [participants, setParticipants] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [leaveApplication, setLeaveApplication] = useState({
    date: '',
    hours: '3',
    userComment: '',
    files: []
  });
  const [leaveDetails, setLeaveDetails] = useState([]); // [{ date: 'YYYY-MM-DD', hours: number }]
  const [filePreviews, setFilePreviews] = useState([]); // [{ url, name, type, file }]
  const [fileViewer, setFileViewer] = useState({ open: false, url: null, name: '', type: '', file: null });
  const [pdfPageNum, setPdfPageNum] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(0);
  const [isNegativeConfirmOpen, setIsNegativeConfirmOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleDateChange = (dateStr) => {
    setLeaveApplication(prev => ({ ...prev, date: dateStr }));
    setIsDirty(true);
  };

  const handleAddDetail = () => {
    if (!leaveApplication.date) {
      setErrorMessage('사용일자를 선택해주세요.');
      setIsErrorModalOpen(true);
      return;
    }
    const parsedHours = parseFloat(leaveApplication.hours);
    if (!Number.isFinite(parsedHours) || parsedHours <= 0) {
      setErrorMessage('사용시간은 0보다 커야 합니다.');
      setIsErrorModalOpen(true);
      return;
    }

    if (leaveDetails.some((d) => d.date === leaveApplication.date)) {
      setErrorMessage('이미 추가된 날짜입니다. 다른 날짜를 선택해 주세요.');
      setIsErrorModalOpen(true);
      return;
    }

    const newDetails = [
      ...leaveDetails,
      { date: leaveApplication.date, hours: parsedHours }
    ].sort((a, b) => a.date.localeCompare(b.date));

    setLeaveDetails(newDetails);
    setLeaveApplication((prev) => ({ ...prev, date: '', hours: '3' }));
    setIsDirty(true);
  };

  const handleRemoveDetail = (index) => {
    setLeaveDetails((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // 프로젝트 목록 조회
  const fetchProjects = useCallback(async () => {
    if (!selectedYear) {
      setProjects([]);
      setSelectedProject('all'); 
      return;
    }
    try {
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
    } catch (err) { 
      console.error("사업 목록 로딩 실패", err); 
      setProjects([]);
      setSelectedProject('all');
    }
  }, [selectedYear]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    if (projects.length > 0) {
      const projectIds = projects.map(p => p.id);
      if (selectedProject !== 'all' && !projectIds.includes(Number(selectedProject))) {
        setSelectedProject('all');
      }
    } else {
      setSelectedProject('all');
    }
  }, [projects]);

  // 참여자 목록 조회
  const fetchParticipants = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        year: selectedYear,
        ...appliedSearch,
      };
      if (selectedProject && selectedProject !== 'all') {
        params.projectId = selectedProject;
      }

      const res = await api.get('/participants', { params });
      setParticipants(res.data); 
    } catch (err) { 
      setError(err.response?.data?.message || '참여자 목록 로딩 실패');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedProject, appliedSearch]);

  // 검색 수행 이후에만 조회
  useEffect(() => {
    if (hasSearched) {
      fetchParticipants();
    }
  }, [fetchParticipants, hasSearched]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchClick = () => {
    setAppliedSearch(searchTerms);
    setHasSearched(true);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleParticipantSelect = (participant) => {
    setSelectedParticipant(participant);
    setLeaveDetails([]);
    setLeaveApplication({ date: '', hours: '3', userComment: '', files: [] });
    setFilePreviews(prev => {
      prev.forEach(p => { try { URL.revokeObjectURL(p.url); } catch (err) {} });
      return [];
    });
    setIsDirty(false); // 모달 열 때 초기화
    setIsModalOpen(true);
  };

  const handleApplicationChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'files') {
      const fileList = Array.from(files || []);
      if (fileList.length > 0) {
        // 용량 및 형식 검증
        for (const f of fileList) {
          const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
          if (!ALLOWED_FILE_TYPES.includes(ext) && !ALLOWED_MIMES.includes(f.type)) {
            setErrorMessage(`첨부할 수 없는 파일 형식입니다.\n\n허용 형식: PDF, JPG, JPEG, PNG, GIF\n선택한 파일: ${f.name}`);
            setIsErrorModalOpen(true);
            e.target.value = '';
            return;
          }
          if (f.size > FILE_MAX_SIZE_BYTES) {
            const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
            setErrorMessage(`첨부파일 용량을 초과했습니다.\n\n파일: ${f.name}\n용량: ${sizeMB}MB\n\n첨부 가능 기준: 파일당 최대 ${FILE_MAX_SIZE_MB}MB`);
            setIsErrorModalOpen(true);
            e.target.value = '';
            return;
          }
        }
        setLeaveApplication(prev => ({ ...prev, files: fileList }));
        setFilePreviews(prev => {
          prev.forEach(p => { try { URL.revokeObjectURL(p.url); } catch (err) {} });
          return [];
        });
        const previews = fileList.map(f => ({
          url: URL.createObjectURL(f),
          name: f.name,
          type: f.type,
          file: f
        }));
        setFilePreviews(previews);
      } else {
        setLeaveApplication(prev => ({ ...prev, files: [] }));
        setFilePreviews(prev => {
          prev.forEach(p => { try { URL.revokeObjectURL(p.url); } catch (err) {} });
          return [];
        });
      }
    } else {
      setLeaveApplication(prev => ({ ...prev, [name]: value }));
    }
    if ((name !== 'files' && value) || (name === 'files' && files && files.length > 0)) {
      setIsDirty(true);
    }
  };

  const doSubmitApplication = async () => {
    try {
      const totalHours = leaveDetails.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0);
      const dateStrings = leaveDetails
        .map((d) => d.date)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)));
      const earliestDateStr = dateStrings[0] || '';

      const formData = new FormData();
      formData.append('participantId', selectedParticipant.id);
      // 레거시 호환을 위해 date/hours도 같이 넣고, 실제 처리는 details로 수행합니다.
      formData.append('date', earliestDateStr);
      formData.append('hours', totalHours);
      formData.append('details', JSON.stringify(leaveDetails));
      if (leaveApplication.userComment) {
        formData.append('userComment', leaveApplication.userComment);
      }
      leaveApplication.files.forEach(file => {
        formData.append('files', file);
      });

      const res = await api.post('/leave/applications', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccessMessage(res.data?.message || '연차 사용 신청이 접수되었습니다.');
      setIsSuccessModalOpen(true);
      setIsDirty(false);
      setIsModalOpen(false);
      setIsNegativeConfirmOpen(false);
      setLeaveApplication({ date: '', hours: '3', userComment: '', files: [] });
      setLeaveDetails([]);
      setFilePreviews(prev => {
        prev.forEach(p => { try { URL.revokeObjectURL(p.url); } catch (err) {} });
        return [];
      });
      fetchParticipants();
    } catch (err) {
      setIsDirty(false);
      setIsNegativeConfirmOpen(false);
      const apiMsg = err.response?.data?.message;
      const apiErr = err.response?.data?.error;
      let displayMsg = '연차 사용 신청 실패';
      if (apiMsg) {
        displayMsg = apiMsg;
      } else if (typeof apiErr === 'string' && (apiErr.includes('File too large') || apiErr.includes('LIMIT_FILE_SIZE'))) {
        displayMsg = `첨부파일 용량을 초과했습니다.\n\n첨부 가능 기준: 파일당 최대 ${FILE_MAX_SIZE_MB}MB\n\n파일 크기를 줄이거나 다른 파일을 선택해 주세요.`;
      } else if (err.response?.status === 413) {
        displayMsg = `첨부파일 용량을 초과했습니다.\n\n첨부 가능 기준: 파일당 최대 ${FILE_MAX_SIZE_MB}MB\n\n파일 크기를 줄이거나 다른 파일을 선택해 주세요.`;
      }
      setErrorMessage(displayMsg);
      setIsErrorModalOpen(true);
    }
  };

  const handleApplicationSubmit = async () => {
    if (!leaveDetails || leaveDetails.length === 0) {
      setErrorMessage('사용일자와 사용시간을 추가해주세요.');
      setIsErrorModalOpen(true);
      return;
    }
    if (!leaveApplication.files || leaveApplication.files.length === 0) {
      setErrorMessage('첨부파일을 첨부해주세요.');
      setIsErrorModalOpen(true);
      return;
    }

    const totalHours = leaveDetails.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0);
    if (totalHours <= 0) {
      setErrorMessage('사용시간은 0보다 커야 합니다.');
      setIsErrorModalOpen(true);
      return;
    }

    const expectedRemaining = (selectedParticipant.remainingLeaveHours || 0) - totalHours;
    if (expectedRemaining < 0) {
      setIsNegativeConfirmOpen(true);
      return;
    }

    await doSubmitApplication();
  };

  const handleCloseModal = () => {
    setIsDirty(false);
    setIsModalOpen(false);
    setSelectedParticipant(null);
    setLeaveDetails([]);
    setLeaveApplication({ date: '', hours: '3', userComment: '', files: [] });
    // 파일 미리보기 blob URL 정리
    setFilePreviews(prev => {
      prev.forEach(p => { try { URL.revokeObjectURL(p.url); } catch (err) {} });
      return [];
    });
    setFileViewer({ open: false, url: null, name: '', type: '', file: null });
  };

  const handleCloseErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorMessage('');
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setSuccessMessage('');
  };

  const totalUsedHours = leaveDetails.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0);
  const totalUsedDays = new Set(leaveDetails.map((d) => d.date).filter(Boolean)).size;
  const totalUsedDayHourLabel = `${totalUsedDays}일 ${totalUsedHours}시간`;
  const formatMonthDay = (ymd) => {
    if (!ymd) return '-';
    const parts = String(ymd).split('-').map((v) => parseInt(v, 10));
    const m = parts[1];
    const d = parts[2];
    if (!Number.isFinite(m) || !Number.isFinite(d)) return ymd;
    return `${m}/${d}`;
  };

  return (
    <div>
      <h2>연차 사용 등록</h2>

      {/* 1. 필터 */}
      <div style={baseStyles.filtersContainer}>
        <div style={baseStyles.filterGroup}>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>연도</span>
            <input
              type="number"
              style={baseStyles.select} 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              placeholder="연도 입력 (예: 2024)"
              min="2020"
              step="1"
            />
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>사업명</span>
            <select style={baseStyles.select} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
              <option value="all">전체</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.projectName}</option>)}
            </select>
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>성명</span>
            <input type="text" name="searchName" placeholder="성명 검색" value={searchTerms.searchName} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={baseStyles.searchInput} />
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>메모</span>
            <input type="text" name="searchMemo" placeholder="메모 검색" value={searchTerms.searchMemo} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={baseStyles.searchInput} />
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>읍면동</span>
            <input
              type="text"
              name="searchDistrict"
              placeholder="읍면동 검색"
              value={searchTerms.searchDistrict}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              style={baseStyles.searchInput}
            />
          </div>
          <div style={{ ...baseStyles.filterItem, marginLeft: 'auto', flexDirection: 'row', gap: '8px' }}>
            <button
              style={{ ...baseStyles.button, backgroundColor: '#2563eb', color: '#fff' }}
              onClick={handleSearchClick}
              disabled={loading}
            >
              🔍 검색
            </button>
          </div>
        </div>

        {/* 인원 표시 */}
        <div style={baseStyles.countDisplay}>
          {loading ? '검색 중...' : `조회된 인원: 총 ${participants.length} 명`}
        </div>
      </div>

      {/* 2. 데이터 테이블 */}
      <div style={baseStyles.tableContainer}>
        {loading ? <p>로딩 중...</p> : (
          <table style={baseStyles.table}>
            <thead>
              <tr>
                <th style={baseStyles.th}>성명</th>
                <th style={baseStyles.th}>주민번호</th>
                <th style={baseStyles.th}>사업명</th>
                <th style={baseStyles.th}>생성연차</th>
                <th style={baseStyles.th}>누적사용연차</th>
                <th style={baseStyles.th}>잔여연차</th>
                <th style={baseStyles.th}>승인중 연차</th>
                <th style={baseStyles.th}>연차신청</th>
              </tr>
            </thead>
            <tbody>
              {participants.length > 0 ? participants.map(p => (
                <tr key={p.id}>
                  <td style={baseStyles.td}>{p.name}</td>
                  <td style={baseStyles.td}>{p.rrn.substring(0, 8)}...</td>
                  <td style={baseStyles.td}>{p.Project?.projectName || p.project?.projectName || p.businessName || '-'}</td>
                  <td style={baseStyles.td}>{p.generatedLeaveHours || 0} h</td>
                  <td style={baseStyles.td}>{p.usedLeaveHours || 0} h</td>
                  <td style={baseStyles.td}>{p.remainingLeaveHours || 0} h</td>
                  <td style={baseStyles.td}>
                    <span style={{ color: 'red', fontWeight: 'bold' }}>
                      {`${p.pendingLeaveHours ?? 0} h`}
                    </span>
                  </td>
                  <td style={baseStyles.td}>
                    <button 
                      style={{...baseStyles.button, ...baseStyles.modalButton}}
                      onClick={() => handleParticipantSelect(p)}
                    >
                      연차사용등록
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" style={baseStyles.td}>검색된 참여자가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 연차 사용 등록 모달 */}
      {isModalOpen && selectedParticipant && (
        <div style={baseStyles.modalOverlay}>
          <div style={baseStyles.modal}>
            <div style={baseStyles.modalHeader}>
              <h3>연차 사용 등록</h3>
              <button onClick={handleCloseModal} style={baseStyles.button}>×</button>
            </div>
            <div style={baseStyles.modalBody}>
              {/* 기본 인적사항 */}
              <div style={baseStyles.formRow}>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>성명</label>
                  <input type="text" value={selectedParticipant.name} readOnly style={baseStyles.formInput} />
                </div>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>주민번호</label>
                  <input type="text" value={selectedParticipant.rrn.substring(0, 8) + '...'} readOnly style={baseStyles.formInput} />
                </div>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>사업명</label>
                  <input type="text" value={selectedParticipant.Project?.projectName || selectedParticipant.project?.projectName || selectedParticipant.businessName || '-'} readOnly style={baseStyles.formInput} />
                </div>
              </div>

              {/* 연차 정보 */}
              <div style={baseStyles.formRow}>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>생성연차</label>
                  <input type="text" value={`${selectedParticipant.generatedLeaveHours || 0} h`} readOnly style={baseStyles.formInput} />
                </div>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>누적사용연차</label>
                  <input type="text" value={`${selectedParticipant.usedLeaveHours || 0} h`} readOnly style={baseStyles.formInput} />
                </div>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>잔여연차</label>
                  <input type="text" value={`${selectedParticipant.remainingLeaveHours || 0} h`} readOnly style={baseStyles.formInput} />
                </div>
                <div style={baseStyles.formCol}>
                  <label style={baseStyles.formLabel}>승인중 연차</label>
                  <input
                    type="text"
                    value={`${parseFloat(selectedParticipant.pendingLeaveHours || 0).toFixed(1)} h`}
                    readOnly
                    style={{
                      ...baseStyles.formInput,
                      color: 'red',
                      fontWeight: 'bold'
                    }}
                  />
                </div>
              </div>

              {/* 신청 정보 */}
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.formLabel}>사용일자</label>
                <DateInput
                  value={leaveApplication.date}
                  onChange={handleDateChange}
                  inputStyle={baseStyles.formInput}
                />
              </div>
              <div style={{ ...baseStyles.formGroup, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 180px', minWidth: 160 }}>
                  <label style={baseStyles.formLabel}>사용시간</label>
                  <input
                    type="number"
                    name="hours"
                    value={leaveApplication.hours}
                    onChange={handleApplicationChange}
                    placeholder="사용시간"
                    style={baseStyles.formInput}
                    min="0.5"
                    step="0.5"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    onClick={handleAddDetail}
                    style={{
                      padding: '8px 14px',
                      border: 'none',
                      borderRadius: 6,
                      background: '#007bff',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    추가
                  </button>
                </div>
              </div>

              <div style={{ fontWeight: 'bold', marginTop: 10 }}>
                총 {totalUsedHours} 시간 사용 ({totalUsedDayHourLabel})
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  border: '1px solid #eee',
                  marginTop: 10,
                  maxHeight: 180,
                  overflowY: 'auto',
                  borderRadius: 6
                }}
              >
                {leaveDetails.length > 0 ? (
                  leaveDetails.map((item, index) => (
                    <li
                      key={`${item.date}_${index}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderBottom: index !== leaveDetails.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}
                    >
                      <span>
                        {formatMonthDay(item.date)} ({item.hours}시간)
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDetail(index)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: 4
                        }}
                      >
                        삭제
                      </button>
                    </li>
                  ))
                ) : (
                  <li style={{ padding: '10px 12px', color: '#666' }}>추가된 날짜가 없습니다.</li>
                )}
              </ul>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.formLabel}>사용 후 예상 잔여연차</label>
                {(() => {
                  const expectedRemaining = (selectedParticipant.remainingLeaveHours || 0) - totalUsedHours;
                  const isNegative = expectedRemaining < 0;
                  return (
                    <input 
                      type="text" 
                      value={`${expectedRemaining.toFixed(1)} h`} 
                      readOnly 
                      style={{...baseStyles.formInput, ...(isNegative ? { color: 'red', fontWeight: 'bold' } : {})}} 
                    />
                  );
                })()}
              </div>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.formLabel}>승인중 연차</label>
                {(() => {
                  const pendingHours = parseFloat(selectedParticipant.pendingLeaveHours || 0);
                  return (
                    <input
                      type="text"
                      value={`${pendingHours.toFixed(1)} h`}
                      readOnly
                      style={{ ...baseStyles.formInput, color: 'red', fontWeight: 'bold' }}
                    />
                  );
                })()}
              </div>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.formLabel}>의견</label>
                <textarea 
                  name="userComment"
                  value={leaveApplication.userComment}
                  onChange={handleApplicationChange}
                  placeholder="의견을 입력하세요 (선택사항)"
                  style={{...baseStyles.formInput, minHeight: '80px', resize: 'vertical'}}
                  rows="3"
                />
              </div>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.formLabel}>첨부파일 (PDF, 이미지) *필수</label>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                  허용 형식: PDF, JPG, PNG, GIF / 파일당 최대 {FILE_MAX_SIZE_MB}MB
                </div>
                <input 
                  type="file" 
                  name="files"
                  onChange={handleApplicationChange}
                  style={baseStyles.fileInput}
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {filePreviews.map((p, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        setPdfPageNum(1);
                        setPdfTotalPages(0);
                        setFileViewer({ open: true, url: p.url, name: p.name, type: p.type, file: p.file });
                      }}
                      style={{
                        cursor: 'pointer',
                        width: 80,
                        textAlign: 'center',
                        padding: 4,
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        background: '#fafafa'
                      }}
                    >
                      {p.type.startsWith('image/') ? (
                        <img src={p.url} alt={p.name} style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 4, display: 'block', margin: '0 auto' }} />
                      ) : (
                        <div style={{ width: 72, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eee', borderRadius: 4, margin: '0 auto', fontSize: 10 }}>PDF</div>
                      )}
                      <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 4 }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>클릭하여 미리보기</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={baseStyles.modalFooter}>
              <button onClick={handleCloseModal} style={baseStyles.button}>취소</button>
              <button onClick={handleApplicationSubmit} style={{...baseStyles.button, ...baseStyles.saveButton}}>
                신청접수
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 성공 모달 */}
      {isSuccessModalOpen && (
        <div style={baseStyles.modalOverlay} onClick={handleCloseSuccessModal}>
          <div style={baseStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={baseStyles.modalHeader}>
              <h3 style={{color: '#28a745', margin: 0}}>✓ 접수 완료</h3>
              <button onClick={handleCloseSuccessModal} style={baseStyles.button}>×</button>
            </div>
            <div style={baseStyles.modalBody}>
              <p style={{color: '#155724', fontSize: '1rem', lineHeight: '1.6'}}>
                {successMessage}
              </p>
            </div>
            <div style={baseStyles.modalFooter}>
              <button 
                onClick={handleCloseSuccessModal} 
                style={{...baseStyles.button, ...baseStyles.saveButton, width: '100%'}}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 첨부파일 미리보기 모달 (PDF는 react-pdf로 렌더링 - Edge 내장 뷰어 미사용) */}
      {fileViewer.open && fileViewer.url && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
          onClick={() => setFileViewer({ open: false, url: null, name: '', type: '', file: null })}
        >
          <div
            style={{ width: '90%', maxWidth: 800, height: '90%', background: '#fff', padding: 12, borderRadius: 8, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontWeight: 'bold', fontSize: 14 }}>{fileViewer.name}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {fileViewer.type === 'application/pdf' && fileViewer.file && (
                  <a
                    href={fileViewer.url}
                    download={fileViewer.name}
                    style={{ ...baseStyles.button, backgroundColor: '#17a2b8', color: 'white', textDecoration: 'none', fontSize: 12 }}
                  >
                    다운로드
                  </a>
                )}
                <button onClick={() => setFileViewer({ open: false, url: null, name: '', type: '', file: null })} style={baseStyles.button}>닫기</button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
              {fileViewer.type === 'application/pdf' ? (
                fileViewer.file ? (
                  <>
                    {pdfTotalPages > 1 && (
                      <div style={{ position: 'absolute', top: 48, left: 12, right: 12, display: 'flex', justifyContent: 'center', gap: 8, zIndex: 10, background: '#fff', padding: '4px 0' }}>
                        <button
                          onClick={() => setPdfPageNum(p => Math.max(1, p - 1))}
                          disabled={pdfPageNum <= 1}
                          style={baseStyles.button}
                        >
                          이전
                        </button>
                        <span style={{ fontSize: 12, lineHeight: '28px' }}>{pdfPageNum} / {pdfTotalPages}</span>
                        <button
                          onClick={() => setPdfPageNum(p => Math.min(pdfTotalPages, p + 1))}
                          disabled={pdfPageNum >= pdfTotalPages}
                          style={baseStyles.button}
                        >
                          다음
                        </button>
                      </div>
                    )}
                    <Document
                      file={fileViewer.file}
                      onLoadSuccess={({ numPages }) => setPdfTotalPages(numPages)}
                      loading="PDF 로딩 중..."
                      error="PDF를 표시할 수 없습니다."
                    >
                      <Page
                        pageNumber={pdfPageNum}
                        width={Math.min(typeof window !== 'undefined' ? window.innerWidth * 0.8 - 48 : 600, 700)}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    </Document>
                  </>
                ) : (
                  <p style={{ color: '#999' }}>PDF 미리보기를 사용할 수 없습니다. 다운로드해 주세요.</p>
                )
              ) : (
                <img src={fileViewer.url} alt={fileViewer.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 잔여연차 마이너스 확인 모달 */}
      {isNegativeConfirmOpen && (
        <div style={baseStyles.modalOverlay} onClick={() => setIsNegativeConfirmOpen(false)}>
          <div style={{ ...baseStyles.modal, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={baseStyles.modalHeader}>
              <h3 style={{ color: '#d9534f', margin: 0 }}>⚠️ 연차 사용 확인</h3>
              <button onClick={() => setIsNegativeConfirmOpen(false)} style={baseStyles.button}>×</button>
            </div>
            <div style={baseStyles.modalBody}>
              <p style={{ fontSize: '1rem', lineHeight: '1.6', color: '#333' }}>
                발생연차보다 사용연차가 많습니다. 그대로 연차 사용을 신청하시겠습니까?
              </p>
            </div>
            <div style={{ ...baseStyles.modalFooter, gap: '10px' }}>
              <button onClick={() => setIsNegativeConfirmOpen(false)} style={baseStyles.button}>
                취소
              </button>
              <button onClick={doSubmitApplication} style={{ ...baseStyles.button, ...baseStyles.saveButton }}>
                접수
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 에러 모달 */}
      {isErrorModalOpen && (
        <div style={baseStyles.modalOverlay} onClick={handleCloseErrorModal}>
          <div style={baseStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={baseStyles.modalHeader}>
              <h3 style={{color: '#d9534f', margin: 0}}>⚠️ 오류</h3>
              <button onClick={handleCloseErrorModal} style={baseStyles.button}>×</button>
            </div>
            <div style={baseStyles.modalBody}>
              <p style={{ color: '#d9534f', fontSize: '1rem', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                {errorMessage}
              </p>
            </div>
            <div style={baseStyles.modalFooter}>
              <button 
                onClick={handleCloseErrorModal} 
                style={{...baseStyles.button, ...baseStyles.saveButton, width: '100%'}}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRegisterPage;